import * as React from 'react';

/**
 * Sweeping the pointer across the rail on the way somewhere else must not fling the list open, and
 * clipping a corner on the way back must not slam it shut.
 */
export const PEEK_OPEN_DELAY_MS = 100;
export const PEEK_CLOSE_DELAY_MS = 140;
const RECHECK_MS = 200;

/**
 * True while a control inside the list has an open popup: the view switcher's menu, the Search
 * criteria panel and its Selects all render outside the pane, so the pointer moving onto one reads
 * as leaving the list. Collapsing then would pull the list out from under the control in use.
 */
const hasOpenPopup = (region: HTMLElement | null) => !!region?.querySelector('[aria-haspopup][aria-expanded="true"]');

/**
 * True while the list holds keyboard focus. Hiding the pane would drop that focus to `<body>`, so an
 * agent who peeked the list and tabbed into it would lose their place the moment the pointer drifted
 * off. Scoped to `:focus-visible` rather than any focus: a mouse click leaves the ticket row focused,
 * and holding for that would keep the list out for the length of the session.
 */
const holdsKeyboardFocus = (region: HTMLElement | null) => !!region?.querySelector(':focus-visible');

/**
 * Hover state for the collapsed ticket list. `enabled` is false whenever the list is shown in full
 * (pinned, phone, table view), where the peek must not fire and any in-flight peek is dropped.
 *
 * Attach `regionRef` to the element that owns both the rail and the pane: the pane is a DOM child,
 * so the pointer moving onto the peeked list is not a leave even though the pane overhangs the
 * rail's box. Attach `railRef` to the rail's toggle — it catches focus left behind in the pane.
 */
export const useTicketListPeek = (enabled: boolean) => {
	const regionRef = React.useRef<HTMLDivElement>(null);
	const railRef = React.useRef<HTMLButtonElement>(null);
	const timerRef = React.useRef<number | undefined>(undefined);
	const [isPeeking, setIsPeeking] = React.useState(false);

	const clearTimer = React.useCallback(() => {
		if (timerRef.current === undefined) return;
		window.clearTimeout(timerRef.current);
		timerRef.current = undefined;
	}, []);

	React.useEffect(() => clearTimer, [clearTimer]);

	React.useEffect(() => {
		if (enabled) return;
		clearTimer();
		setIsPeeking(false);
	}, [clearTimer, enabled]);

	const openPeek = React.useCallback(() => {
		if (!enabled) return;
		clearTimer();
		timerRef.current = window.setTimeout(() => {
			timerRef.current = undefined;
			setIsPeeking(true);
		}, PEEK_OPEN_DELAY_MS);
	}, [clearTimer, enabled]);

	const closePeek = React.useCallback(() => {
		clearTimer();
		const collapse = () => {
			const region = regionRef.current;
			if (hasOpenPopup(region) || holdsKeyboardFocus(region)) {
				timerRef.current = window.setTimeout(collapse, RECHECK_MS);
				return;
			}
			timerRef.current = undefined;
			// Focus parked on a clicked row would go to <body> as the pane hides; the rail is the
			// list in its collapsed state, so tabbing on from there stays in place.
			if (region?.contains(document.activeElement)) railRef.current?.focus();
			setIsPeeking(false);
		};
		timerRef.current = window.setTimeout(collapse, PEEK_CLOSE_DELAY_MS);
	}, [clearTimer]);

	return { regionRef, railRef, isPeeking: enabled && isPeeking, openPeek, closePeek } as const;
};
